import React from 'react';
import PropTypes from 'prop-types';
import { get, keyBy } from 'lodash';
import { ZCDIcon, ZCDTable } from '@zencity/common-ui';
import { useTranslation } from 'react-i18next';
import ExpandableTable from './ExpandableTable';
import SentimentBar from './SentimentBar';

const calculatePercentages = (sentimentCounts, subtotal) => {
  const { positive, neutral, negative, mixed, none } = sentimentCounts;
  return {
    positivePercent: parseFloat(((positive / subtotal) * 100).toFixed(2)),
    neutralPercent: parseFloat(((neutral / subtotal) * 100).toFixed(2)),
    negativePercent: parseFloat(((negative / subtotal) * 100).toFixed(2)),
    mixedPercent: parseFloat(((mixed / subtotal) * 100).toFixed(2)),
    nonePercent: parseFloat(((none / subtotal) * 100).toFixed(2)),
  };
};

const columns = [
  { header: 'Pin', accessorKey: 'label' },
  { header: 'Count', accessorKey: 'count' },
  { header: '%', accessorKey: 'percentage' },
  {
    header: 'Sentiment',
    accessorKey: 'sentiment',
    cell: ({ row }) => {
      return row.original.sentiment;
    },
  },
  {
    id: 'expander',
    header: () => {
      return <span>More</span>;
    },
    accessorKey: 'Expend',
    cell: ({ row }) => {
      return row.getCanExpand() ? (
        <span
          {...{
            onClick: row.getToggleExpandedHandler(),
            style: {
              paddingLeft: `${row.depth * 2}rem`,
            },
          }}
        >
          {row.getIsExpanded() ? <ZCDIcon name="chevron-down" /> : <ZCDIcon name="chevron-right" />}
        </span>
      ) : null;
    },
  },
];

/**
 * Display the pin option data table with sentiment breakdown
 */
const PinOptionDataTableWithSentimentData = (props) => {
  const { inputs, counts, stats } = props;
  const { t } = useTranslation();
  const inputsKeyById = keyBy(inputs, 'id');

  const renderSentimentBar = (pinOptionSentimentCount) => {
    const { sentimentCounts, subtotal, inputId } = pinOptionSentimentCount;
    const { positive, neutral, negative, mixed } = sentimentCounts;
    const input = inputsKeyById[inputId];
    const inputMetadata = input?.metadata;
    const allowComment = inputsKeyById[inputId] ? get(inputMetadata, 'allowComment', false) : false;
    const { positivePercent, neutralPercent, negativePercent, mixedPercent } = calculatePercentages(
      sentimentCounts,
      subtotal
    );

    return (
      <SentimentBar
        allowComment={allowComment}
        positive={positive}
        positivePercent={positivePercent}
        mixed={mixed}
        mixedPercent={mixedPercent}
        neutral={neutral}
        neutralPercent={neutralPercent}
        negative={negative}
        negativePercent={negativePercent}
      />
    );
  };

  const tableData = counts.map((pinOptionSentimentCount) => ({
    label: pinOptionSentimentCount.label,
    count: pinOptionSentimentCount.subtotal,
    percentage: ((pinOptionSentimentCount.subtotal / pinOptionSentimentCount.total) * 100).toFixed(
      2
    ),
    sentiment: renderSentimentBar(pinOptionSentimentCount),
    stats: stats[pinOptionSentimentCount.inputId] || [],
    allowComment: inputsKeyById[pinOptionSentimentCount.inputId]
      ? get(inputsKeyById[pinOptionSentimentCount.inputId].metadata, 'allowComment', false)
      : false,
  }));

  const renderSubComponent = ({ row }) => {
    const columnArray = [
      { Header: '#', accessor: 'position' },
      { Header: t('topKeywords.word'), accessor: 'word' },
      { Header: t('topKeywords.responses'), accessor: 'ndoc' },
      { Header: t('topKeywords.mentions'), accessor: 'nentry' },
    ];
    const statsData = row.original.stats.map((stat, index) => ({
      ...stat,
      position: index + 1,
    }));

    return (
      <div>
        <ZCDTable columnArray={columnArray} tableData={statsData} maxItemsLimit={10} />
      </div>
    );
  };

  return (
    <div className="pin-option-data-table-with-sentiment-data">
      <ExpandableTable
        data={tableData}
        columns={columns}
        renderSubComponent={renderSubComponent}
        getRowCanExpand={(row) => row.original.allowComment}
      />
    </div>
  );
};

PinOptionDataTableWithSentimentData.propTypes = {
  inputs: PropTypes.arrayOf(PropTypes.shape({})).isRequired,
  counts: PropTypes.arrayOf(PropTypes.shape({})).isRequired,
  stats: PropTypes.shape({}).isRequired,
};

export default PinOptionDataTableWithSentimentData;
